import curry from 'lodash/curry';
import { css, FlattenSimpleInterpolation } from 'styled-components';

import IThemeInterface from '../../Theme/common';

import { normalizeColor } from './normalizeColor';

export type VALID_BORDER_SIDES =
  | 'all'
  | 'bottom'
  | 'end'
  | 'horizontal'
  | 'left'
  | 'right'
  | 'start'
  | 'top'
  | 'noTop'
  | 'noBottom'
  | 'noLeft'
  | 'noRight'
  | 'noVertical'
  | 'noHorizontal'
  | 'vertical';

export type VALID_BORDER_STYLES =
  | 'dashed'
  | 'dotted'
  | 'double'
  | 'groove'
  | 'ridge'
  | 'inset'
  | 'outset'
  | 'hidden'
  | 'solid';

export interface IBorderStyleData {
  color?: string;
  side?: VALID_BORDER_SIDES;
  size?: string;
  style?: VALID_BORDER_STYLES;
}

const VALID_NO_SIDE_STRINGS = [
  'noTop',
  'noBottom',
  'noLeft',
  'noRight',
  'noVertical',
  'noHorizontal',
];

export type VALID_BORDER_DATA = VALID_BORDER_SIDES | IBorderStyleData | string;

export const calcBorderStyles = curry((
  theme: IThemeInterface,
  data: VALID_BORDER_DATA,
) => {
  const styles: FlattenSimpleInterpolation[] = [];

  if (typeof data === 'string' && !data) {
    return undefined;
  }

  if (typeof data === 'string' && data === 'none') {
    return css`
      border: none;
    `;
  }

  let color = normalizeColor(theme, 'black');
  let size = 'single';
  let style = 'solid';
  let side = 'all';

  if (typeof data === 'string') {
    side = data;

  } else {
    color = data.color ? normalizeColor(theme, data.color) : color;
    size = data.size ? data.size : size;
    style = data.style ? data.style : style;
    side = data.side ? data.side : side;
  }

  const value = `${theme.global.edgeSize[size] || size} ${style} ${color}`;

  if (
    // tslint:disable-next-line: prefer-switch
    side === 'top' ||
    side === 'bottom' ||
    side === 'left' ||
    side === 'right'
  ) {
    styles.push(css`
      border-${side}: ${value};
    `);

  } else if (side === 'end' || side === 'start') {
    styles.push(css`
      border-inline-${side}: ${value};
    `);

  } else if (side === 'vertical') {
    styles.push(css`
      border-left: ${value};
      border-right: ${value};
    `);

  } else if (side === 'horizontal') {
    styles.push(css`
      border-top: ${value};
      border-bottom: ${value};
    `);

  } else if (VALID_NO_SIDE_STRINGS.includes(side)) {
    const normalizedSide = side
      .replace('no', '')
      .toLowerCase();

    styles.push(css`
      border: ${value};
      border-${normalizedSide}: none;
    `);
  } else {
    styles.push(css`
        border: ${value};
      `,
    );
  }

  return styles;
});
